import React from 'react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

interface ChartCardProps {
  title: string;
  data: any[];
  type: 'line' | 'area' | 'bar' | 'pie';
  loading?: boolean;
  className?: string;
  height?: number;
  xDataKey?: string;
  yDataKey?: string;
  color?: string;
  colors?: string[];
  showGrid?: boolean;
  showTooltip?: boolean;
  formatTooltip?: (value: any, name: string) => [string, string];
  dateFormat?: 'daily' | 'weekly' | 'monthly' | 'yearly';
  valueType?: 'currency' | 'number' | 'percentage';
}

const ChartCard: React.FC<ChartCardProps> = ({
  title,
  data,
  type,
  loading = false,
  className = '',
  height = 300,
  xDataKey = 'date',
  yDataKey = 'value',
  color = '#3C50E0',
  colors = ['#3C50E0', '#06B6D4', '#10B981', '#F59E0B', '#EF4444'],
  showGrid = true,
  showTooltip = true,
  formatTooltip,
  dateFormat = 'daily',
  valueType = 'number'
}) => {
  const defaultColors = ['#3C50E0', '#06B6D4', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6B7280'];

  // Date formatting utilities
  const formatDateLabel = (dateStr: string): string => {
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;

      switch (dateFormat) {
        case 'daily':
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        case 'weekly':
          return `Week ${Math.ceil(date.getDate() / 7)}`;
        case 'monthly':
          return date.toLocaleDateString('en-US', { month: 'short' });
        case 'yearly':
          return date.getFullYear().toString();
        default:
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
    } catch {
      return dateStr;
    }
  };

  const formatTooltipDate = (dateStr: string): string => {
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  // Value formatting utilities
  const formatYAxisValue = (value: number): string => {
    if (valueType === 'currency') {
      if (value >= 1000000) return `₹${(value / 1000000).toFixed(1)}M`;
      if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
      return `₹${value}`;
    }
    if (valueType === 'percentage') {
      return `${value}%`;
    }
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toString();
  };

  const formatTooltipValue = (value: number): string => {
    if (valueType === 'currency') {
      return `₹${value.toLocaleString('en-IN')}`;
    }
    if (valueType === 'percentage') {
      return `${value}%`;
    }
    return value.toLocaleString('en-IN');
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-boxdark p-3 border border-stroke dark:border-strokedark rounded-md shadow-lg">
          <p className="text-sm font-medium text-black dark:text-white mb-2">
            {formatTooltipDate(label)}
          </p>
          {payload.map((entry: any, index: number) => {
            const value = entry.value;
            const name = entry.name || entry.dataKey;
            
            // Use custom formatter if provided, otherwise use built-in formatting
            const formattedValue = formatTooltip 
              ? formatTooltip(value, name)[0]
              : formatTooltipValue(value);
            
            const displayName = formatTooltip 
              ? formatTooltip(value, name)[1]
              : name.charAt(0).toUpperCase() + name.slice(1);

            return (
              <div key={index} className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-sm text-meta-6">{displayName}</span>
                </div>
                <span className="text-sm font-medium text-black dark:text-white">
                  {formattedValue}
                </span>
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  const renderChart = () => {
    switch (type) {
      case 'line':
        return (
          <LineChart data={data}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />}
            <XAxis 
              dataKey={xDataKey} 
              stroke="#6B7280"
              fontSize={12}
              tick={{ fill: '#6B7280' }}
              tickFormatter={formatDateLabel}
              interval="preserveStartEnd"
            />
            <YAxis 
              stroke="#6B7280"
              fontSize={12}
              tick={{ fill: '#6B7280' }}
              tickFormatter={formatYAxisValue}
            />
            {showTooltip && <Tooltip content={<CustomTooltip />} />}
            <Line 
              type="monotone" 
              dataKey={yDataKey} 
              stroke={color}
              strokeWidth={3}
              dot={{ fill: color, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        );

      case 'area':
        return (
          <AreaChart data={data}>
            <defs>
              <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={color} stopOpacity={0}/>
              </linearGradient>
            </defs>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />}
            <XAxis 
              dataKey={xDataKey} 
              stroke="#6B7280"
              fontSize={12}
              tick={{ fill: '#6B7280' }}
              tickFormatter={formatDateLabel}
              interval="preserveStartEnd"
            />
            <YAxis 
              stroke="#6B7280"
              fontSize={12}
              tick={{ fill: '#6B7280' }}
              tickFormatter={formatYAxisValue}
            />
            {showTooltip && <Tooltip content={<CustomTooltip />} />}
            <Area 
              type="monotone" 
              dataKey={yDataKey} 
              stroke={color}
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#colorGradient)"
            />
          </AreaChart>
        );

      case 'bar':
        return (
          <BarChart data={data}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />}
            <XAxis 
              dataKey={xDataKey} 
              stroke="#6B7280"
              fontSize={12}
              tick={{ fill: '#6B7280' }}
              tickFormatter={formatDateLabel}
              interval="preserveStartEnd"
            />
            <YAxis 
              stroke="#6B7280"
              fontSize={12}
              tick={{ fill: '#6B7280' }}
              tickFormatter={formatYAxisValue}
            />
            {showTooltip && <Tooltip content={<CustomTooltip />} />}
            <Bar 
              dataKey={yDataKey} 
              fill={color}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        );

      case 'pie':
        return (
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              outerRadius={80}
              fill="#8884d8"
              dataKey={yDataKey}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={defaultColors[index % defaultColors.length]} />
              ))}
            </Pie>
            {showTooltip && <Tooltip content={<CustomTooltip />} />}
          </PieChart>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className={`rounded-sm border border-stroke bg-white p-6 shadow-default dark:border-strokedark dark:bg-boxdark ${className}`}>
        <div className="mb-4">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-300 rounded w-1/3 mb-2"></div>
          </div>
        </div>
        <div className="animate-pulse" style={{ height: height }}>
          <div className="bg-gray-300 rounded w-full h-full flex items-center justify-center">
            <div className="text-gray-500">Loading chart...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className={`rounded-sm border border-stroke bg-white p-6 shadow-default dark:border-strokedark dark:bg-boxdark ${className}`}>
        <div className="mb-4">
          <h4 className="text-xl font-semibold text-black dark:text-white">
            {title}
          </h4>
        </div>
        <div className="flex items-center justify-center" style={{ height: height }}>
          <div className="text-center">
            <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2Zm0 0V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v10m-6 0a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2m0 0V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v14a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v10Z" />
            </svg>
            <p className="text-gray-500 dark:text-gray-400">No data available</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-sm border border-stroke bg-white p-6 shadow-default dark:border-strokedark dark:bg-boxdark ${className}`}>
      <div className="mb-4">
        <h4 className="text-xl font-semibold text-black dark:text-white">
          {title}
        </h4>
      </div>
      <div style={{ height: height }}>
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ChartCard;